/**
* @copyright Copyright (c) MITSUBISHI Electric Corporation. All rights reserved.
*/

import React, { useContext, useState } from "react"
import { IndexContext } from "../IndexProvider"
import { Modal } from "react-bootstrap"
import ROLES, { ROLES_DESCRIPTION } from "../consts/roles"
import SERVICES, { SERVICES_DESCRIPTION } from "../consts/services"
import validate from 'validate.js'
import PAGES from "../consts/pages"

const accountNameConstraints = {
    accountName: {
        presence: {
            allowEmpty: false
        },
        format: {
            pattern: /^[\S\s]{1,50}$/
        }
    }
};

const accountIdConstraints = {
    accountId: {
        presence: {
            allowEmpty: false
        },
        email: true
    }
};

const accountKanaConstraints = {
    accountKana: {
        presence: {
            allowEmpty: false
        },
        format: {
            pattern: /^[\p{scx=Hiragana}\s/　/・/ー]{1,50}$/u
        }
    }
};

export default function AccountList() {
    const [searchField, setSearchField] = useState("byId")
    const [searchText, setSearchText] = useState("")
    const [modalShow, setModalShow] = useState(false)
    const [modalTitle, setModalTitle] = useState("")
    const [isPending, setIsPending] = useState(false)

    const [accountName, setAccountName] = useState("");
    const [accountId, setAccountId] = useState("")
    const [accountKana, setAccountKana] = useState("")
    const [maxDeviceNum, setMaxDeviceNum] = useState(1)
    const [accountRole, setAccountRole] = useState(ROLES.GENERAL_USER)
    const [accountServices, setAccountServices] = useState([])

    const [isAccountNameValid, setIsAccountNameValid] = useState(false)
    const [isAccountIdValid, setIsAccountIdValid] = useState(false)
    const [isAccountKanaValid, setIsAccountKanaValid] = useState(false)
    const [shouldShowAccountNameFeedback, setShouldShowAccountNameFeedback] = useState(false)
    const [shouldShowAccountIdFeedback, setShouldShowAccountIdFeedback] = useState(false)
    const [shouldShowAccountKanaFeedback, setShouldShowAccountKanaFeedback] = useState(false)


    const { accountDetails, accountList, selectedAccount, setSelectedAccount, updateAccount, deleteAccount, createAccount, breadcrumbs, setBreadcrumbs, pageLoading, setCurrentPage, selectedTenant } = useContext(IndexContext)

    const onSearchBtnClick = (e) => {
        e.preventDefault()
        e.stopPropagation()
    }

    const onClearBtnClick = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setSearchText("")
    }

    const onSearchFormSubmit = (e) => {
        e.preventDefault()
        e.stopPropagation()
    }

    const buildFormControlClasses = (shouldShowFeedback, isValid) => {
        if (shouldShowFeedback) {
            return `form-control ${isValid ? "is-valid" : "is-invalid"}`
        } else {
            return "form-control"
        }
    }


    const createAccountElementList = () => {
        if (!accountList) return []
        let filteredAccountList = searchText ? accountList.filter((account) => {
            if (searchField === "byId") {
                return account.account_id.includes(searchText)
            } else {
                return account.account_name.includes(searchText)
            }
        }) : accountList.slice()

        const elementList = filteredAccountList && filteredAccountList.map((account, idx) => {
            // console.log(account)
            return (
                <tr key={idx + 1}>
                    <th scope="row">{idx}</th>
                    <td><a style={{ cursor: "pointer" }} onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setSelectedAccount(account)
                        setBreadcrumbs(breadcrumbs.concat([{ title: `アカウント詳細`, path: PAGES.ACCOUNT }]))
                        setCurrentPage(PAGES.ACCOUNT)
                    }} >{account.account_name}</a></td>
                    <td>{ROLES_DESCRIPTION[account.role]}</td>
                    <td>
                        <ul style={{ paddingLeft: 0 }}>
                            {
                                account.service_id_list.map((id, i) => (<li key={id}>{SERVICES_DESCRIPTION[id]}</li>))
                            }
                        </ul>
                    </td>
                    <td>{account.status ? '有効' : '無効'}</td>
                    {
                        accountDetails.role === ROLES.SYSTEM_ADMIN && account.account_id !== accountDetails.account_id ?
                            <td style={{ width: 160 }}>
                                <button className="btn btn-outline-warning me-2" onClick={(e) => {
                                    setModalTitle("アカウント" + (account.status ? '無効化' : '有効化'))
                                    setSelectedAccount(account)
                                    setModalShow(true)
                                }} >
                                    {
                                        account.status ? '無効化' : '有効化'
                                    }
                                </button>
                                <button className="btn btn-outline-danger" onClick={(e) => {
                                    setModalTitle("アカウント削除")
                                    setSelectedAccount(account)
                                    setModalShow(true)
                                }} >削除</button>
                            </td> : <td></td>
                    }
                </tr>
            );
        })

        return elementList
    }

    const onAccountNameChange = (event) => {
        setAccountName(event.target.value)
        setShouldShowAccountNameFeedback(true)
        if (validate({ accountName: event.target.value }, accountNameConstraints)) {
            setIsAccountNameValid(false)
        } else {
            setIsAccountNameValid(true)
        }
    }

    const onAccountIdChange = (event) => {
        setAccountId(event.target.value)
        setShouldShowAccountIdFeedback(true)
        if (validate({ accountId: event.target.value }, accountIdConstraints)) {
            setIsAccountIdValid(false)
        } else {
            setIsAccountIdValid(true)
        }
    }

    const onAccountKanaChange = (event) => {
        setAccountKana(event.target.value)
        setShouldShowAccountKanaFeedback(true)
        if (validate({ accountKana: event.target.value }, accountKanaConstraints)) {
            setIsAccountKanaValid(false)
        } else {
            setIsAccountKanaValid(true)
        }
    }

    const onMaxDeviceNumChange = (event) => {
        setMaxDeviceNum(event.target.value)
    }

    const onAccountRoleChange = (event) => {
        setAccountRole(event.target.value)
    }

    const onRulerlessWebSwitchChange = (event) => {
        let services = accountServices.filter((s) => {
            return s !== SERVICES.RULERLESS
        })
        if (event.target.checked) {
            services.push(SERVICES.RULERLESS)
        }

        setAccountServices(services)
    }

    const onRulerlessIosSwitchChange = (event) => {
        let services = accountServices.filter((s) => {
            return s !== SERVICES.RULERLESS_IOS
        })
        if (event.target.checked) {
            services.push(SERVICES.RULERLESS_IOS)
        }

        setAccountServices(services)
    }

    const onRulerlessIosOfflineSwitchChange = (event) => {
        let services = accountServices.filter((s) => {
            return s !== SERVICES.RULERLESS_IOS_OFFLINE
        })
        if (event.target.checked) {
            services.push(SERVICES.RULERLESS_IOS_OFFLINE)
        }

        setAccountServices(services)
    }

    const onRulerlessTruckBaggageSwitchChange = (event) => {
        let services = accountServices.filter((s) => {
            return s !== SERVICES.RULERLESS_TRUCK_BAGGAGE
        })
        if (event.target.checked) {
            services.push(SERVICES.RULERLESS_TRUCK_BAGGAGE)
        }

        setAccountServices(services)
    }

    const createModalBody = () => {
        if (modalTitle === "アカウント無効化" || modalTitle === "アカウント有効化") {
            const actionTitle = (selectedAccount.status ? '無効化' : '有効化')
            return (
                <div>{`アカウント名：${selectedAccount.account_name}を${actionTitle}します。宜しいですか？`}</div>
            )
        } else if (modalTitle === "アカウント削除") {
            return (
                <div>{`アカウント名：${selectedAccount.account_name}を削除します。宜しいですか？`}</div>
            )
        } else if (modalTitle === "アカウント登録") {
            return (
                <>
                    <div className="mb-3 row" >
                        <div className="col-12 col-sm-2 d-flex align-items-center" >
                            <label htmlFor="accountNameInput">アカウントID</label>
                        </div>
                        <div className="col-12 col-sm-10 ">
                            <input className={buildFormControlClasses(shouldShowAccountIdFeedback, isAccountIdValid)} onChange={onAccountIdChange} value={accountId} id="accountNameInput" type="email" />
                            <div className="invalid-feedback">
                                有効なメルアドレースを入力してください。
                            </div>
                        </div>
                    </div>
                    <div className="mb-3 row">
                        <div className="col-12 col-sm-2 d-flex align-items-center">
                            <label htmlFor="accountNameInput">アカウント名</label>
                        </div>
                        <div className="col-12 col-sm-10">
                            <input className={buildFormControlClasses(shouldShowAccountNameFeedback, isAccountNameValid)} onChange={onAccountNameChange} value={accountName} id="accountNameInput" type="text" />
                            <div className="invalid-feedback">
                                アカウント名を入力してください。
                            </div>
                        </div>
                    </div>
                    <div className="mb-3 row" >
                        <div className="col-12 col-sm-2 d-flex align-items-center">
                            <label htmlFor="accountKanaInput">アカウント名（かな）</label>
                        </div>
                        <div className="col-12 col-sm-10">
                            <input className={buildFormControlClasses(shouldShowAccountKanaFeedback, isAccountKanaValid)} onChange={onAccountKanaChange} value={accountKana} id="accountKanaInput" type="text" />
                            <div className="invalid-feedback">
                                アカウント名（かな）を入力してください。
                            </div>
                        </div>
                    </div>
                    {/*<div className="mb-3 row" >
                        <div className="col-12 col-sm-2 d-flex align-items-center">
                            <label htmlFor="maxDeviceNumInput">最大デバイス数</label>
                        </div>
                        <div className="col-12 col-sm-10">
                            <input className="form-control" type="number" value={maxDeviceNum} id="maxDeviceNumInput" onChange={onMaxDeviceNumChange} min="1" max="10" />
                        </div>
                    </div>*/}
                    <div className="mb-3 row" >
                        <div className="col-12 col-sm-2 d-flex align-items-center">
                            <label htmlFor="accountRoleInput">ロール</label>
                        </div>
                        <div className="col-12 col-sm-10">
                            <select className="form-select" id="accountRoleInput" value={accountRole} onChange={onAccountRoleChange}>

                                {
                                    [ROLES.GENERAL_USER, ROLES.TENANT_ADMIN, ROLES.SYSTEM_ADMIN].map((role, idx) => {
                                        if (idx === 0) {
                                            return <option value={role} active="true" key={idx}>{ROLES_DESCRIPTION[role]}</option>
                                        } else {
                                            return (
                                                <option value={role} key={idx}>{ROLES_DESCRIPTION[role]}</option>
                                            )
                                        }

                                    })
                                }
                            </select>
                        </div>
                    </div>
                    <div className="mb-3 row" >
                        <div className="col-12 col-sm-2 d-flex align-items-center" >
                            <label>使用サービス</label>
                        </div>
                        <div className="col-12 col-sm-10 ">
                            <div className="form-check form-switch">
                                <input className="form-check-input" type="checkbox" role="switch" id="rulerlessWebSwitch" onChange={onRulerlessWebSwitchChange} />
                                <label className="form-check-label" htmlFor="rulerlessWebSwitch">Rulerless Web</label>
                            </div>
                            <div className="form-check form-switch">
                                <input className="form-check-input" type="checkbox" role="switch" id="rulerlessIosSwitch" onChange={onRulerlessIosSwitchChange} />
                                <label className="form-check-label" htmlFor="rulerlessIosSwitch">Rulerless IOS</label>
                            </div>
                            <div className="form-check form-switch">
                                <input className="form-check-input" type="checkbox" role="switch" id="rulerlessIosOfflineSwitch" onChange={onRulerlessIosOfflineSwitchChange} />
                                <label className="form-check-label" htmlFor="rulerlessIosOfflineSwitch">{"Rulerless IOS(Offline)"}</label>
                            </div>
                            <div className="form-check form-switch">
                                <input className="form-check-input" type="checkbox" role="switch" id="rulerlessTruckBaggageSwitch" onChange={onRulerlessTruckBaggageSwitchChange} />
                                <label className="form-check-label" htmlFor="rulerlessTruckBaggageSwitch">Rulerless 積載率機能</label>
                            </div>
                        </div>
                    </div>
                </>
            )
        }
    }

    const onAccountEditBtnClick = async (e) => {
        setIsPending(true)
        let account = Object.assign({}, selectedAccount);
        account.status = !account.status;
        await updateAccount(account)
        setIsPending(false)
        setModalShow(false)
    }

    const onAccountDeleteBtnClick = async (e) => {
        setIsPending(true)
        const data = {
            'account_list': [{
                'account_id': selectedAccount.account_id,
            }]
        }
        await deleteAccount(data)
        setIsPending(false)
        setModalShow(false)
    }

    const onAccountCreateBtnClick = async (e) => {
        setIsPending(true)
        const now = new Date().toISOString();
        const data = {
            account_list: [
                {
                    'tenant_id': selectedTenant.tenant_id,
                    'account_id': accountId,
                    'account_name': accountName,
                    'account_name_kana': accountKana,
                    'max_login_device': parseInt(maxDeviceNum),
                    'role': accountRole,
                    'service_id_list': accountServices,
                    'last_login_at': now
                }
            ]
        }
        await createAccount(data)
        setIsPending(false)
        setModalShow(false)
        data.account_list[0].status = true
        setSelectedAccount(data.account_list[0])
        setBreadcrumbs(breadcrumbs.concat([{ title: `アカウント詳細`, path: PAGES.ACCOUNT }]))
        setCurrentPage(PAGES.ACCOUNT)
    }

    const createModalFooter = () => {

        let actionBtn
        if (modalTitle === "アカウント無効化" || modalTitle === "アカウント有効化") {
            actionBtn = isPending ? <button className="btn btn-outline-warning" disabled >変更中...</button> : <button className="btn btn-outline-warning" onClick={onAccountEditBtnClick} >変更</button>
        } else if (modalTitle === "アカウント削除") {
            actionBtn = isPending ? <button className="btn btn-outline-danger" disabled >削除中...</button> : <button className="btn btn-outline-danger" onClick={onAccountDeleteBtnClick} >削除</button>
        } else if (modalTitle === "アカウント登録") {
            actionBtn = isPending ? <button className="btn btn-outline-danger" disabled >登録中...</button> : <button className="btn btn-outline-danger" onClick={onAccountCreateBtnClick} >登録</button>
        }

        const cancelBtn = isPending ? <button className="btn btn-outline-secondary ms-3" disabled >キャンセル</button> : <button className="btn btn-outline-secondary ms-3" onClick={(e) => setModalShow(false)} >キャンセル</button>

        return (
            <>
                {cancelBtn}
                {actionBtn}
            </>
        )
    }

    const createFormElement = (isSkeleton) => {

        const firstDiv =
            <div className="col-12 col-sm-6 col-md-2 mb-3 mb-md-0" style={{ visibility: isSkeleton ? "hidden" : "visible" }}>
                {
                    isSkeleton ?
                        <select className="form-select" ></select>
                        :
                        <select className="form-select" value={searchField} onChange={(e) => {
                            setSearchField(e.target.value)
                        }}>
                            <option value="byId" active="true">IDでフィルター</option>
                            <option value="byName">名前でフィルター</option>
                        </select>
                }
            </div>

        const secondDiv =
            <div className="col-12 col-sm-6 col-md-4 mb-3 mb-md-0" style={{ visibility: isSkeleton ? "hidden" : "visible" }}>
                {
                    isSkeleton ?
                        <input className="form-control" />
                        :
                        <input type="text" className="form-control" id="searchInput" placeholder="アカウントを洗い出す" value={searchText} onChange={(e) => {
                            setSearchText(e.target.value)
                        }}></input>
                }
            </div>

        const thirdDiv =
            <div className="col-6 col-md-4" style={{ visibility: isSkeleton ? "hidden" : "visible" }}>
                {
                    isSkeleton ?
                        <>
                            <button className="btn btn-primary">skeleton</button>
                            <button className="btn btn-primary">skeleton</button>
                        </>
                        :
                        <>
                            <button className="btn btn-outline-primary me-3" onClick={onSearchBtnClick} >フィルター</button>
                            <button className="btn btn-outline-primary" onClick={onClearBtnClick} >クリア</button>
                        </>
                }
            </div>

        const fourthDiv =
            <div className="col-6 col-md-2" style={{ visibility: isSkeleton ? "hidden" : "visible" }}>
                {
                    isSkeleton ?
                        <button className="btn btn-primary">skeleton</button>
                        :
                        <button className="btn btn-outline-primary" onClick={(e) => {
                            setModalShow(true)
                            setModalTitle("アカウント登録")
                        }} >新規登録</button>
                }
            </div>


        return (
            <>
                {firstDiv}
                {secondDiv}
                {thirdDiv}
                {fourthDiv}
            </>
        )
    }

    const createTableElement = (isSkeleton) => {
        return (
            <div className={"table-responsive" + (isSkeleton ? " skeleton-1" : "")}>
                <table className="table table-striped" style={{ visibility: isSkeleton ? "hidden" : "visible" }}>
                    <thead style={{ borderTopWidth: 1 }}>
                        <tr>
                            <th scope="col">#</th>
                            <th scope="col">アカウント名</th>
                            <th scope="col">ロール</th>
                            <th scope="col">使用サービス</th>
                            <th scope="col">ステータス</th>
                            <th scope="col">アクション</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            isSkeleton ?
                                <>
                                    <tr>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                    </tr>
                                    <tr>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                    </tr>
                                    <tr>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                    </tr>
                                    <tr>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                        <td>skeleton</td>
                                    </tr>
                                </>
                                :
                                createAccountElementList()
                        }
                    </tbody>
                </table>
            </div>
        )
    }

    return (
        pageLoading ?
            <div className="p-3" >
                <div>
                    <div className="row ms-0 me-0 mb-3 skeleton-1" >
                        {createFormElement(true)}
                    </div>
                </div>
                {createTableElement(true)}
            </div>
            :
            <div className="p-3" >
                <form onSubmit={onSearchFormSubmit}>
                    <div className="row mb-3" >
                        {createFormElement()}
                    </div>
                </form>
                {createTableElement()}
                <Modal size='lg' show={modalShow} onHide={() => setModalShow(false)} aria-labelledby='contained-modal-title-vcenter' centered >
                    <div className='ModalModal'>
                        <Modal.Header closeButton>
                            <Modal.Title>{modalTitle}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {
                                createModalBody()
                            }
                        </Modal.Body>
                        <Modal.Footer>
                            {
                                createModalFooter()
                            }
                        </Modal.Footer>
                    </div>
                </Modal>

            </div>
    );
}